/**
 * Rejected Mail View
 *
 * Persistent admin surface for messages that Rspamd rejected, soft-rejected,
 * greylisted, spam-tagged, or had their subject rewritten. Backed by
 * `edgemail.rejections` (populated every 5 minutes by the ingestion worker).
 *
 * Per-row actions are context-aware on action type:
 *   add_header / rewrite_subject  → Release to Inbox · Block · Whitelist
 *   already released              → View in Inbox · Block · Whitelist
 *   reject                        → Whitelist
 *   soft reject / greylist        → Whitelist · Block
 *
 * Bulk select supports Release / Block (whitelist intentionally omitted —
 * whitelisting is a per-mailbox, per-sender decision; bulk allowlisting is
 * a footgun).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ShieldExclamationIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  NoSymbolIcon,
  ArrowDownTrayIcon,
  CheckBadgeIcon,
  EllipsisVerticalIcon,
  ArrowUturnLeftIcon,
  HandRaisedIcon,
} from '@heroicons/react/24/outline';
import EdgeMailAdminAPI, {
  type RejectedMailRow,
  type RejectedMailDetail,
  type RejectedMailAction,
  type ListRejectedMailParams,
} from '../../services/EdgeMailAdminAPI';

const ACTION_FILTERS: Array<{ id: RejectedMailAction; label: string }> = [
  { id: 'reject', label: 'Reject' },
  { id: 'soft reject', label: 'Soft reject' },
  { id: 'greylist', label: 'Greylist' },
  { id: 'add header', label: 'Spam tag' },
  { id: 'rewrite subject', label: 'Subject rewrite' },
];

const ACTION_PILL: Record<RejectedMailAction, string> = {
  reject:
    'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
  'soft reject':
    'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300',
  greylist:
    'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
  'add header':
    'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
  'rewrite subject':
    'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
};

const ACTION_LABEL: Record<RejectedMailAction, string> = {
  reject: 'Reject',
  'soft reject': 'Soft reject',
  greylist: 'Greylist',
  'add header': 'Spam tag',
  'rewrite subject': 'Subject rewrite',
};

type TimeRange = '24h' | '7d' | '30d' | 'all';

const TIME_RANGES: Array<{ id: TimeRange; label: string }> = [
  { id: '24h', label: 'Last 24h' },
  { id: '7d', label: 'Last 7d' },
  { id: '30d', label: 'Last 30d' },
  { id: 'all', label: 'All' },
];

const RELEASABLE_ACTIONS: ReadonlySet<RejectedMailAction> = new Set([
  'add header',
  'rewrite subject',
]);

function formatRelative(unix: number): string {
  const diffSec = Math.round(Date.now() / 1000 - unix);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.round(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)} h ago`;
  if (diffSec < 7 * 86400) return `${Math.round(diffSec / 86400)} d ago`;
  return new Date(unix * 1000).toLocaleDateString();
}

function formatScore(score: number, required: number | null): string {
  const s = score.toFixed(2);
  if (required === null || required === undefined) return s;
  return `${s} / ${required.toFixed(2)}`;
}

function senderDomain(addr: string | null | undefined): string {
  if (!addr) return '';
  const at = addr.lastIndexOf('@');
  if (at < 0) return addr;
  return addr.slice(at + 1);
}

const RejectedMailView: React.FC = () => {
  const [rows, setRows] = useState<RejectedMailRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [textFilter, setTextFilter] = useState('');
  const [actionFilter, setActionFilter] = useState<Set<RejectedMailAction>>(
    () => new Set(ACTION_FILTERS.map((a) => a.id)),
  );
  const [timeRange, setTimeRange] = useState<TimeRange>('7d');
  const [selected, setSelected] = useState<RejectedMailRow | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const buildParams = useCallback((): ListRejectedMailParams => {
    const since =
      timeRange === '24h'
        ? Math.floor(Date.now() / 1000) - 24 * 3600
        : timeRange === '7d'
          ? Math.floor(Date.now() / 1000) - 7 * 86400
          : timeRange === '30d'
            ? Math.floor(Date.now() / 1000) - 30 * 86400
            : undefined;
    return {
      actions: Array.from(actionFilter),
      since,
      limit: 1000,
    };
  }, [actionFilter, timeRange]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await EdgeMailAdminAPI.listRejectedMail(buildParams());
      setRows(data);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rejected mail');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = textFilter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.sender.toLowerCase().includes(q) ||
        r.recipient.toLowerCase().includes(q) ||
        r.subject.toLowerCase().includes(q),
    );
  }, [rows, textFilter]);

  const toggleAction = useCallback((id: RejectedMailAction) => {
    setActionFilter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        if (next.size === 1) return prev;
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const handleBlockSender = useCallback(
    async (row: RejectedMailRow) => {
      const senderAddr = row.sender || row.senderHeader;
      if (!senderAddr) return;
      try {
        await EdgeMailAdminAPI.createBlockedSender({
          sender_address: senderAddr.toLowerCase(),
          reason: 'spam',
        });
        showToast(`Blocked ${senderAddr}`);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to block sender');
      }
    },
    [showToast],
  );

  const handleWhitelistSender = useCallback(
    async (row: RejectedMailRow) => {
      try {
        await EdgeMailAdminAPI.whitelistFromRejection(row.rejectionId);
        showToast(`Whitelisted ${row.sender}`);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to whitelist sender');
      }
    },
    [showToast],
  );

  const handleRelease = useCallback(
    async (row: RejectedMailRow) => {
      try {
        const result = await EdgeMailAdminAPI.releaseRejection(row.rejectionId);
        if (result.released) {
          showToast(`Released to Inbox`);
          // Optimistic UI update — mark the row as released without refetching.
          setRows((prev) =>
            prev
              ? prev.map((r) =>
                  r.rejectionId === row.rejectionId
                    ? {
                        ...r,
                        releasedAt: new Date().toISOString(),
                        releasedToMessageId: result.messageId ?? null,
                      }
                    : r,
                )
              : prev,
          );
        } else {
          setError(result.reason ?? 'Release failed');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Release failed');
      }
    },
    [showToast],
  );

  const toggleRowSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size > 0) return new Set();
      return new Set(filtered.map((r) => r.rejectionId));
    });
  }, [filtered]);

  const handleBulkRelease = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds).slice(0, 100);
    try {
      const results = await EdgeMailAdminAPI.bulkReleaseRejections(ids);
      const releasedCount = results.filter((r) => r.released).length;
      const skipped = results.length - releasedCount;
      showToast(`Released ${releasedCount}${skipped > 0 ? ` (${skipped} skipped)` : ''}`);
      void refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk release failed');
    }
  }, [selectedIds, refresh, showToast]);

  const handleBulkBlock = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds).slice(0, 100);
    try {
      const results = await EdgeMailAdminAPI.bulkBlockFromRejections(ids);
      const ok = results.filter((r) => r.ok).length;
      showToast(`Blocked ${ok} sender${ok === 1 ? '' : 's'}`);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk block failed');
    }
  }, [selectedIds, showToast]);

  const handleExport = useCallback(() => {
    const url = EdgeMailAdminAPI.getRejectedMailExportUrl(buildParams());
    // Server uses Authorization header from the same axios client; fetch via
    // axios then trigger download to keep auth intact.
    void (async () => {
      try {
        const token = localStorage.getItem('webelieve_access_token') ?? '';
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`Export failed: ${res.status}`);
        const blob = await res.blob();
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        a.href = objectUrl;
        a.download = `rejected-mail-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Export failed');
      }
    })();
  }, [buildParams]);

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <ShieldExclamationIcon className="w-5 h-5 text-red-500" />
            Rejected Mail
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Mail rejected, greylisted, or spam-tagged. Persisted full history; admin can release, block, or whitelist.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
            title="Export current view as CSV"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={() => void refresh()}
            className="p-2 text-gray-500 hover:text-gray-900 dark:hover:text-gray-100 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
            title="Refresh"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={textFilter}
            onChange={(e) => setTextFilter(e.target.value)}
            placeholder="Filter by sender, recipient, or subject…"
            className="w-full pl-9 pr-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400"
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {ACTION_FILTERS.map((a) => {
            const active = actionFilter.has(a.id);
            return (
              <button
                key={a.id}
                onClick={() => toggleAction(a.id)}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  active
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-300 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {a.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1.5">
          {TIME_RANGES.map((t) => {
            const active = timeRange === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTimeRange(t.id)}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  active
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-300 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Bulk action bar (visible when at least one row selected) */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <span className="text-sm text-blue-700 dark:text-blue-300 flex-1">
            {selectedIds.size} row{selectedIds.size === 1 ? '' : 's'} selected
            {selectedIds.size > 100 && ' (limited to 100 per action)'}
          </span>
          <button
            onClick={() => void handleBulkRelease()}
            className="flex items-center gap-1 px-3 py-1 text-xs rounded-md bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
            Release to Inbox
          </button>
          <button
            onClick={() => void handleBulkBlock()}
            className="flex items-center gap-1 px-3 py-1 text-xs rounded-md bg-red-600 hover:bg-red-700 text-white"
          >
            <NoSymbolIcon className="w-3.5 h-3.5" />
            Block senders
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:underline"
          >
            Clear
          </button>
        </div>
      )}

      {error && (
        <div className="px-3 py-2 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm flex items-center gap-2">
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700" aria-label="Dismiss">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-900">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr className="text-left">
              <th className="px-3 py-2 w-8">
                <input
                  type="checkbox"
                  checked={selectedIds.size > 0 && selectedIds.size === filtered.length}
                  onChange={toggleSelectAll}
                  ref={(el) => {
                    if (el) {
                      el.indeterminate =
                        selectedIds.size > 0 && selectedIds.size < filtered.length;
                    }
                  }}
                  className="rounded border-gray-300"
                />
              </th>
              <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-400 w-28">When</th>
              <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-400 w-32">Action</th>
              <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-400 w-28">Score</th>
              <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Sender</th>
              <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Recipient</th>
              <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Subject</th>
              <th className="px-4 py-2 w-12" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {filtered.length === 0 && !loading && (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-10 text-center text-gray-500 dark:text-gray-400"
                >
                  {rows === null
                    ? 'Loading…'
                    : textFilter
                      ? 'No rejections match your filter.'
                      : 'No rejections in the selected window.'}
                </td>
              </tr>
            )}
            {filtered.map((row) => {
              const isReleased = Boolean(row.releasedAt);
              const isReleasable = RELEASABLE_ACTIONS.has(row.action) && !isReleased;
              return (
                <tr
                  key={row.rejectionId}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-800/30 cursor-pointer ${
                    selectedIds.has(row.rejectionId) ? 'bg-blue-50 dark:bg-blue-900/10' : ''
                  }`}
                  onClick={() => setSelected(row)}
                >
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.rejectionId)}
                      onChange={() => toggleRowSelection(row.rejectionId)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="px-4 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    <div>{formatRelative(row.unixTime)}</div>
                    {isReleased && (
                      <div className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                        <CheckBadgeIcon className="w-3 h-3" />
                        Released
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${ACTION_PILL[row.action]}`}
                    >
                      {ACTION_LABEL[row.action]}
                    </span>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-900 dark:text-gray-100">
                    {formatScore(row.score, row.requiredScore)}
                  </td>
                  <td
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 truncate max-w-[260px]"
                    title={row.senderHeader ? `${row.sender}\n(header: ${row.senderHeader})` : row.sender}
                  >
                    {row.sender}
                  </td>
                  <td
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 truncate max-w-[200px]"
                    title={row.recipient}
                  >
                    {row.recipient}
                  </td>
                  <td
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 truncate max-w-[280px]"
                    title={row.subject}
                  >
                    {row.subject || <span className="italic text-gray-400">(no subject)</span>}
                  </td>
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    <RowActionMenu
                      row={row}
                      isReleasable={isReleasable}
                      onRelease={() => void handleRelease(row)}
                      onBlock={() => void handleBlockSender(row)}
                      onWhitelist={() => void handleWhitelistSender(row)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400 dark:text-gray-500">
        Persisted history. Ingestion runs every 5 minutes from Rspamd /history.
      </p>

      {/* Detail drawer */}
      {selected && (
        <RejectedMailDetailDrawer
          row={selected}
          onClose={() => setSelected(null)}
          onBlockSender={() => void handleBlockSender(selected)}
          onWhitelistSender={() => void handleWhitelistSender(selected)}
          onRelease={() => void handleRelease(selected)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 px-4 py-2 rounded-md bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-sm shadow-lg z-[60]">
          {toast}
        </div>
      )}
    </div>
  );
};

const RowActionMenu: React.FC<{
  row: RejectedMailRow;
  isReleasable: boolean;
  onRelease: () => void;
  onBlock: () => void;
  onWhitelist: () => void;
}> = ({ row, isReleasable, onRelease, onBlock, onWhitelist }) => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const handler = () => setOpen(false);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [open]);

  return (
    <div className="relative inline-block">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
        aria-label="Actions"
      >
        <EllipsisVerticalIcon className="w-4 h-4" />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 py-1"
          onClick={(e) => e.stopPropagation()}
        >
          {isReleasable && (
            <button
              onClick={() => {
                onRelease();
                setOpen(false);
              }}
              className="w-full text-left px-3 py-1.5 text-sm text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 flex items-center gap-2"
            >
              <ArrowUturnLeftIcon className="w-4 h-4" />
              Release to Inbox
            </button>
          )}
          {row.action !== 'reject' && (
            <button
              onClick={() => {
                onBlock();
                setOpen(false);
              }}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
            >
              <NoSymbolIcon className="w-4 h-4" />
              Block sender
            </button>
          )}
          <button
            onClick={() => {
              onWhitelist();
              setOpen(false);
            }}
            className="w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
          >
            <HandRaisedIcon className="w-4 h-4" />
            Whitelist sender
          </button>
        </div>
      )}
    </div>
  );
};

const RejectedMailDetailDrawer: React.FC<{
  row: RejectedMailRow;
  onClose: () => void;
  onBlockSender: () => void;
  onWhitelistSender: () => void;
  onRelease: () => void;
}> = ({ row, onClose, onBlockSender, onWhitelistSender, onRelease }) => {
  const [detail, setDetail] = useState<RejectedMailDetail | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    let cancelled = false;
    EdgeMailAdminAPI.getRejectedMailDetail(row.rejectionId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoadErr(err instanceof Error ? err.message : 'Failed to load details');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [row.rejectionId]);

  const symbols = detail?.allSymbols ?? row.topSymbols;
  const isReleased = Boolean(detail?.releasedAt ?? row.releasedAt);
  const isReleasable = RELEASABLE_ACTIONS.has(row.action) && !isReleased;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl h-full overflow-y-auto bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-5 py-3 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <span
              className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${ACTION_PILL[row.action]}`}
            >
              {ACTION_LABEL[row.action]}
            </span>
            <span>{formatScore(row.score, row.requiredScore)}</span>
            {isReleased && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300">
                <CheckBadgeIcon className="w-3 h-3" />
                Released
              </span>
            )}
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <section className="space-y-1.5 text-sm">
            <DetailField label="Subject" value={row.subject || '(no subject)'} />
            <DetailField label="Sender (envelope)" value={row.sender} mono />
            {row.senderHeader && (
              <DetailField label="Sender (header From)" value={row.senderHeader} mono />
            )}
            <DetailField label="Recipient (envelope)" value={row.recipient} mono />
            {row.recipientHeader && (
              <DetailField label="Recipient (header To)" value={row.recipientHeader} mono />
            )}
            <DetailField label="Sender domain" value={senderDomain(row.sender) || '—'} mono />
            <DetailField label="Sender IP" value={row.ip ?? '—'} mono />
            <DetailField
              label="Size"
              value={row.size === null ? '—' : `${(row.size / 1024).toFixed(1)} KB`}
            />
            <DetailField label="Time" value={new Date(row.unixTime * 1000).toLocaleString()} />
            <DetailField label="Message-ID" value={row.messageId} mono />
            {detail?.releasedAt && (
              <DetailField
                label="Released at"
                value={new Date(detail.releasedAt).toLocaleString()}
              />
            )}
          </section>

          {/* Action buttons */}
          <div className="flex flex-wrap items-center gap-2">
            {isReleasable && (
              <button
                onClick={onRelease}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <ArrowUturnLeftIcon className="w-4 h-4" />
                Release to Inbox
              </button>
            )}
            {row.action !== 'reject' && (
              <button
                onClick={onBlockSender}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-red-600 hover:bg-red-700 text-white"
              >
                <NoSymbolIcon className="w-4 h-4" />
                Block sender
              </button>
            )}
            <button
              onClick={onWhitelistSender}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <HandRaisedIcon className="w-4 h-4" />
              Whitelist sender
            </button>
          </div>

          {/* Symbols */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Spam-score symbols ({symbols.length})
              </h4>
              {detail && (
                <button
                  onClick={() => setShowRaw((v) => !v)}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {showRaw ? 'Hide raw JSON' : 'View raw JSON'}
                </button>
              )}
            </div>
            {loadErr && (
              <div className="mb-2 px-3 py-2 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-xs">
                {loadErr} (showing top 5 only)
              </div>
            )}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr className="text-left">
                    <th className="px-3 py-1.5 font-medium text-gray-600 dark:text-gray-400">Symbol</th>
                    <th className="px-3 py-1.5 font-medium text-gray-600 dark:text-gray-400 w-16 text-right">
                      Score
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {symbols.map((s) => (
                    <tr key={s.name}>
                      <td className="px-3 py-1.5 align-top">
                        <div className="font-mono text-gray-900 dark:text-gray-100">{s.name}</div>
                        {s.description && (
                          <div className="text-gray-500 dark:text-gray-400 mt-0.5">
                            {s.description}
                          </div>
                        )}
                      </td>
                      <td
                        className={`px-3 py-1.5 text-right font-mono align-top ${
                          s.score > 0
                            ? 'text-red-600 dark:text-red-400'
                            : s.score < 0
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {s.score > 0 ? '+' : ''}
                        {s.score.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {showRaw && detail && (
              <pre className="mt-3 max-h-72 overflow-auto p-3 text-[11px] bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-gray-800 dark:text-gray-200">
                {JSON.stringify(detail, null, 2)}
              </pre>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

const DetailField: React.FC<{ label: string; value: string; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <div className="grid grid-cols-[140px_1fr] gap-3">
    <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 pt-0.5">
      {label}
    </div>
    <div
      className={`text-gray-900 dark:text-gray-100 break-all ${mono ? 'font-mono text-xs' : ''}`}
    >
      {value}
    </div>
  </div>
);

export default RejectedMailView;
